/**
 * layers.ts — THE one place a layering number is written (2026-09-11).
 *
 * WHY THIS FILE EXISTS
 * ---------------------------------------------------------------------------
 * A user in the chat's full-screen mode pressed the in-chat button to sign
 * into Gmail. The "which inbox?" prompt opened BEHIND the chat and the button
 * said "connecting…" forever. The prompt is a PanelModal, PanelModal defaulted
 * to 1000, and the full-screen chat is 9,999,999.
 *
 * That was the seventh time. The record, in order: the ⓘ popup "not clickable"
 * in expanded mode, then the Archive-All modal, the chat tooltips, the image
 * lightbox and the attachment panel (all 2026-07-13); the desktop sign-in
 * surface at 1,200 opening "invisibly UNDER the chat pane that launched it";
 * the setup-fields dialog (2026-08-25); the reaction picker at 10,050
 * (2026-09-09). Each was fixed one component at a time, by picking a bigger
 * number, and each fix left the next one buried.
 *
 * The reason it kept happening is that there was no ladder — only four
 * unrelated module constants and about ninety bare literals, with the
 * contract written as prose in whichever file last thought about it. By
 * 2026-09-11 that prose had drifted from the code in three places at once:
 * windowGeometry.ts and its test both asserted the chat renders at
 * 2,147,483,000 (it is 9,999,999, so the test proved nothing); the chat's own
 * comment said the docked pane sits "still below TopUpModal", which stopped
 * being the useful fact once full-screen existed; and the sign-in modal's
 * comment mis-stated three of the four numbers it listed.
 *
 * So: every number lives here, once. Modules keep their existing export names
 * and re-export from this file, so no import site had to change. Nothing was
 * renumbered when this landed — the values below are exactly what shipped
 * before it, which is what keeps the deliberate relations (see CHAT_DOCKED)
 * intact.
 *
 * HOW TO USE IT
 * ---------------------------------------------------------------------------
 * Pick the rung by what the thing IS, never by "what number clears the thing
 * I can see today":
 *
 *   - a menu, tooltip or sheet belonging to the chat   → BODY_PORTAL_Z_INDEX
 *   - a passive strip that must not cover a dialog     → APP_NOTICE_Z_INDEX
 *   - a modal/dialog/popup openable while chat is up   → APP_OVERLAY_Z_INDEX
 *   - a tooltip that may annotate one of those         → APP_TOOLTIP_Z_INDEX
 *   - page furniture deliberately under the takeover   → a ledger entry in
 *     config/overlay-layers.d/ saying why
 *
 * `scripts/check-overlay-layers.mjs` enforces all of it: no bare layering
 * literal outside this file, no number from this ladder restated in code or
 * comment anywhere else, and every body portal wearing the click marker.
 * That gate is the reason a comment here can be trusted.
 */

/**
 * The chat's PAGE-LEVEL chrome, which sits in the page rather than over it:
 * the prompt bar a closed chat leaves behind, the collapsed docked rail, and
 * the floating launcher. Deliberately under every pane rung — they are what
 * the pane replaces, never something that has to clear it.
 */
export const CHAT_LAUNCHER_Z_INDEX = 1_100;
/** In-pane: the docked prompt bar sits in the pane's own flow, not over it. */
export const CHAT_IN_PANE_PROMPT_BAR_Z_INDEX = 1_151;
export const CHAT_PROMPT_BAR_Z_INDEX = 1_500;

/**
 * The bar the sign-in window folds into while the person signs in on the /desktop page in the same tab
 * (2026-10-06, PR 4 of the DocuSign fix). It belongs to that page: above the page and its fixed chat bar, and
 * below the docked chat, the header and every chat surface, so a chat brought back over the page covers it as it
 * covers the page.
 */
export const DESKTOP_SIGNIN_BAR_Z_INDEX = CHAT_PROMPT_BAR_Z_INDEX + 1;

/**
 * DOCKED pane (owner directive 2026-08-24: top-nav dropdown menus must open
 * ON TOP of the docked chat, never behind it). While docked the chat is page
 * chrome — the page reflows beside it — so it sits BELOW the app header's
 * stacking context (APP_HEADER_Z_INDEX, the next rung). That one relation is what
 * lets every menu inside the header paint above the pane, and it is the
 * reason this rung must never drift upward with the rest of the ladder.
 */
export const CHAT_DOCKED_Z_INDEX = 14_500;

/**
 * THE APP HEADER's own stacking context: today's (Header.tsx HeaderContainer) and the new design's
 * (design/pavilion/app, the root it stands in). The docked pane (the rung before) must stay under it.
 */
export const APP_HEADER_Z_INDEX = 15_000;

/**
 * FLOATING overlay — transient, outside-click-closed, and never sharing the
 * screen with an open nav menu the way the pinned pane does.
 */
export const CHAT_FLOATING_Z_INDEX = 500_000;

/**
 * FULL-SCREEN takeover. Everything a user can still reach while this is up
 * has to clear it; that is the whole content of this file.
 */
export const CHAT_FULLSCREEN_Z_INDEX = 9_999_999;

/**
 * The ONE global banner band pins itself above the takeover and publishes its
 * measured height as --prest0n-banner-band-height, which the takeover insets
 * below (2026-08-31 consistency directive).
 */
export const BANNER_BAND_PINNED_Z_INDEX = CHAT_FULLSCREEN_Z_INDEX + 1;

/**
 * The chat's OWN body-portaled surfaces: menus, tooltips, the thread sheet,
 * the image lightbox, confirmations, the reaction picker. Above the band as
 * well as the pane, so a menu opened from full-screen clears both.
 */
export const BODY_PORTAL_Z_INDEX = CHAT_FULLSCREEN_Z_INDEX + 2;

/**
 * Passive strips — platform messages. Above the chat in every mode (owner
 * decision 2026-09-11: a notice invisible behind the takeover never reaches
 * someone who lives in full-screen chat) but BELOW the modal rung, because a
 * notice must never cover a dialog the user is answering.
 */
export const APP_NOTICE_Z_INDEX = CHAT_FULLSCREEN_Z_INDEX + 3;

/**
 * App-level modals and popups — the default rung for anything openable while
 * the chat is up: the shared Modal, every PanelModal (which-inbox, setup
 * fields, access controls, manage app, folder scope), the top-up modal, the
 * redeem shell. If you are adding a popup and are not sure, it is this one.
 */
export const APP_OVERLAY_Z_INDEX = 20_000_000;

/** The "the window will say Composio" coach, pointing at the OAuth window. */
export const CONNECT_COACH_Z_INDEX = APP_OVERLAY_Z_INDEX + 1;

/** Role/permission explainer popovers. */
export const INFO_POPOVER_Z_INDEX = APP_OVERLAY_Z_INDEX + 10;

/** Tooltips, which must clear a modal they annotate. */
export const APP_TOOLTIP_Z_INDEX = APP_OVERLAY_Z_INDEX + 20;

/**
 * The in-page desktop sign-in surface (a cross-origin noVNC view of Prest0n's
 * own browser). Above the coach that describes it and above every other
 * overlay: it is the surface a half-finished sign-in lives in.
 */
export const DESKTOP_SIGNIN_Z_INDEX = APP_OVERLAY_Z_INDEX + 100;

/**
 * Onboarding's name-entry popup, which opens ON TOP of the catalog modal it
 * was launched from. Its own rung because it is the one place in the app that
 * legitimately stacks a second modal over a first — it used to borrow the
 * desktop sign-in surface's number, which made the two impossible to tell
 * apart and either impossible to move safely (2026-09-11).
 */
export const ONBOARDING_STACKED_MODAL_Z_INDEX = DESKTOP_SIGNIN_Z_INDEX + 1;

/**
 * File previews (2026-08-02 directive: a preview is a real window that stays
 * with the user while they keep working). They deliberately top the app —
 * including the chat — because the user dragged them out to multitask.
 */
export const PREVIEW_MODAL_Z = 2_147_483_200;
export const FLOATING_PREVIEW_BASE_Z = 2_147_483_300;

/**
 * Boot chrome — the splash, the load-failure overlay and the 3D runtime
 * guard, all near int32 max. Declared here for the record; the first two live
 * in public/index.html and cannot import anything.
 */
export const BOOT_OVERLAY_Z_INDEX = 2_147_483_646;
export const BOOT_FAILURE_Z_INDEX = 2_147_483_647;
